/* eslint-disable @typescript-eslint/no-empty-object-type */
import { Button } from "@/components/generic/Button";
import { DynamicField } from "@/components/generic/DynamicField";
import { ExportInput, Inputs } from "@/types/integrations";
import { MaybePromise } from "@/types/maybe-promise";
import { findControllingField } from "@/utils/integration-helpers";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { cva, VariantProps } from "cva";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Control, FormProvider, useForm, useWatch } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";

interface IntegrationFormProps<T extends {}> {
  className?: string;
  size?: VariantProps<typeof gridStyles>["size"];
  inputs: Inputs;
  onSubmit?: (
    data: Record<string, unknown>,
    customData: T,
  ) => MaybePromise<void>;
  onSkip?: () => void;
  onReset?: () => void;
  showSubmitButton?: boolean;
  disableSubmit?: boolean;
  children?:
    | React.ReactNode
    | ((renderProps: {
        control: Control<IntegrationFormValues<T>>;
        formData: IntegrationFormValues<T>;
      }) => React.ReactNode);
  defaultValues?: Record<string, string>;
  values?: Record<string, string>;
  showSkip?: boolean;
  includeVisible?: boolean;
  canReset?: boolean;
  resetLabel?: string;
  submitLabel?: string;

  customFields?: {
    schema: z.ZodType<T, T>;
    defaultValues: T;
    values?: T;
  };
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  autoSubmit?: boolean;
}

interface IntegrationFormValues<T extends {}> {
  fields: Record<string, unknown>;
  customFields: T;
}

const gridStyles = cva({
  base: "w-full grid gap-x-4 gap-y-10",
  variants: {
    size: {
      xs: "grid-cols-1",
      sm: "grid-cols-2",
      md: "grid-cols-3",
    },
  },
  defaultVariants: { size: "xs" },
});

// ---------------------------------------------------------------------------
// Pure field-evaluation helpers (no React state; safe to call from anywhere)
// ---------------------------------------------------------------------------

/** Evaluates a condition string (e.g. "fieldId=value" or "fieldId!=value")
 *  against the runtime value of the controlling field. */
function evaluateCondition(
  conditionStr: string,
  controllingInput: ExportInput,
  controllingValue: unknown,
): boolean {
  const match = conditionStr.match(/^(.+?)(=|!=)(.+)$/);
  if (match) {
    const [, , comparator, expectedValue] = match;
    if (controllingInput.type === "checkbox") {
      const boolStr = controllingValue ? "true" : "false";
      return comparator === "="
        ? expectedValue === boolStr
        : expectedValue !== boolStr;
    }
    const strValue = String(controllingValue);
    return comparator === "="
      ? strValue === expectedValue
      : strValue !== expectedValue;
  }
  return Boolean(controllingValue);
}

/** Returns whether a field should be visible given the full set of field values.
 *
 * `isControllingField` skips the `visible` property check so that hidden fields
 * can still control other fields' visibility when `includeVisible` is false. */
function getFieldVisibility(
  input: ExportInput,
  allFields: ExportInput[],
  fields: Map<string, unknown>,
  includeVisible: boolean,
  isControllingField = false,
): boolean {
  if (!isControllingField && !includeVisible && input.visible === false)
    return false;
  if (!input.showIfTrue) return true;

  const controllingInput = allFields.find(
    findControllingField(input, "showIfTrue"),
  );
  if (!controllingInput) return true;

  // Recursive check — always skip the visible property for controlling fields
  // so hidden controller fields can still affect other fields' visibility.
  if (
    !getFieldVisibility(
      controllingInput,
      allFields,
      fields,
      includeVisible,
      true,
    )
  )
    return false;

  return evaluateCondition(
    input.showIfTrue,
    controllingInput,
    fields.get(controllingInput.id),
  );
}

/** Returns whether a field is required given the full set of field values. */
function getFieldRequirement(
  input: ExportInput,
  allFields: ExportInput[],
  fields: Map<string, unknown>,
): boolean {
  if (!input.requiredIfTrue) return input.mandatory;

  const controllingField = allFields.find(
    findControllingField(input, "requiredIfTrue"),
  );
  if (!controllingField) return input.mandatory;

  return evaluateCondition(
    input.requiredIfTrue,
    controllingField,
    fields.get(controllingField.id),
  );
}

// ---------------------------------------------------------------------------

export function IntegrationForm<T extends {}>({
  className,
  children,
  size,
  inputs,
  defaultValues,
  values,
  includeVisible = true,
  showSkip = false,
  canReset = false,
  showSubmitButton = true,
  disableSubmit = false,
  resetLabel = "Deaktiver integration",
  submitLabel = "Gem og afslut",
  onReset,
  onSkip,
  onSubmit,

  customFields,
  setSubmitHandler,
  autoSubmit = false,
}: IntegrationFormProps<T>) {
  const allFields = useMemo(
    () =>
      // Spread before sort to avoid mutating the prop array in-place
      [...inputs.steps]
        .sort((a, b) => a.stepNumber - b.stepNumber)
        .flatMap((step) => step.inputs),
    [inputs],
  );

  const fieldsById = useMemo(
    () => new Map(allFields.map((input) => [input.id, input])),
    [allFields],
  );

  // superRefine calls pure helpers directly with the data being validated —
  // no closure over reactive state, so there is no stale-closure hazard.
  const formSchema = useMemo(() => {
    const fieldSchemaMap = Object.fromEntries(
      allFields.map((input) => [input.id, z.unknown().nullable()]),
    );

    return z.object({
      fields: z.object(fieldSchemaMap).superRefine((data, ctx) => {
        const formDataById = new Map(Object.entries(data));

        for (const [key, value] of Object.entries(data)) {
          const field = fieldsById.get(key);
          if (!field) continue;

          if (
            !getFieldVisibility(field, allFields, formDataById, includeVisible)
          )
            continue;

          if (
            getFieldRequirement(field, allFields, formDataById) &&
            (value === undefined || value === null || value === "")
          ) {
            ctx.addIssue({
              code: "custom",
              message: `${field.name} er påkrævet`,
              path: [field.id],
            });
          }
        }
      }),
      customFields: customFields?.schema ?? (z.any() as z.ZodType<T, T>),
    });
  }, [allFields, fieldsById, includeVisible, customFields?.schema]);

  const formDefaultValues = useMemo(
    () => ({
      fields: Object.fromEntries(
        allFields.map((input) => [
          input.id,
          defaultValues?.[input.id] ?? (input.defaultValue || null),
        ]),
      ),
      customFields: customFields?.defaultValues ?? ({} as T),
    }),
    [allFields, customFields?.defaultValues, defaultValues],
  );

  const {
    watch,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    subscribe,
    ...methods
  } = useForm<IntegrationFormValues<T>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: formDefaultValues,
  });

  // useWatch on "fields" subscribes to field value changes via useState and
  // guarantees a re-render whenever any field value changes — unlike watch(),
  // whose re-renders depend solely on formState changes (errors, isValid, etc.).
  // Without this, valuesById would be stale for optional fields that never
  // produce validation errors, breaking conditional visibility logic.
  const watchedFields = useWatch({ control, name: "fields" });

  // watch() returns the full, correctly-typed form values and is used for
  // the children render prop. It reads fresh values on each render that
  // useWatch above already triggered.
  const watchedFormData = watch();

  // Sync external defaultValues into the form while preserving user input.
  // The formDefaultValues memo only changes when the underlying props change,
  // so this never fires unnecessarily.
  useEffect(() => {
    reset(formDefaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  useEffect(() => {
    if (!values) return;
    for (const [key, value] of Object.entries(values)) {
      setValue(`fields.${key}`, value);
    }
  }, [setValue, values]);

  useEffect(() => {
    if (!customFields?.values) return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setValue("customFields", customFields.values);
  }, [setValue, customFields]);

  // Trigger initial validation so the submit button state is correct from mount.
  useEffect(() => {
    methods.trigger("fields");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valuesById = useMemo(
    () => new Map<string, unknown>(Object.entries(watchedFields ?? {})),
    [watchedFields],
  );

  // Reactive wrappers around the pure helpers — used for rendering decisions.
  const isFieldVisible = useCallback(
    (input: ExportInput) =>
      getFieldVisibility(input, allFields, valuesById, includeVisible),
    [allFields, valuesById, includeVisible],
  );

  const isFieldRequired = useCallback(
    (input: ExportInput) => getFieldRequirement(input, allFields, valuesById),
    [allFields, valuesById],
  );

  const getVisibleFields = useCallback(
    (inputs: ExportInput[]) =>
      inputs.filter((i) => isFieldVisible(i)).sort((a, b) => a.order - b.order),
    [isFieldVisible],
  );

  const visibleFields = useMemo(
    () =>
      allFields
        .filter((i) => isFieldVisible(i))
        .sort((a, b) => a.order - b.order),
    [allFields, isFieldVisible],
  );

  const showSkipButton = useMemo(
    () => showSkip && valuesById.get("createengagementdropdown") !== "always",
    [showSkip, valuesById],
  );

  const submit = useMemo(
    () => handleSubmit((data) => onSubmit?.(data.fields, data.customFields)),
    [onSubmit, handleSubmit],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDisabled:
        !methods.formState.isValid ||
        disableSubmit ||
        methods.formState.isSubmitting,
      isDirty: methods.formState.isDirty,
      isPending: methods.formState.isSubmitting,
    });

    return () =>
      setSubmitHandler?.({
        submit: () => {},
      });
  }, [
    disableSubmit,
    methods.formState.isValid,
    methods.formState.isSubmitting,
    methods.formState.isDirty,
    setSubmitHandler,
    submit,
  ]);

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
  }, [autoSubmit, submit]);

  // #region Debugging

  const debugInfo = useMemo(
    () =>
      JSON.stringify(
        {
          visibleInputsCount: visibleFields.length,
          errors: methods.formState.errors,
          formValues: watchedFields,
        },
        null,
        2,
      ),
    [visibleFields.length, methods.formState.errors, watchedFields],
  );
  // #endregion

  return (
    <FormProvider
      {...methods}
      watch={watch}
      handleSubmit={handleSubmit}
      control={control}
      reset={reset}
      getValues={getValues}
      setValue={setValue}
      subscribe={subscribe}
    >
      <form
        className={twMerge(
          "flex flex-col items-center justify-center gap-y-16",
          className,
        )}
        onSubmit={submit}
      >
        {inputs.steps
          .filter(
            (s) =>
              s.inputs.filter(
                (i) => isFieldVisible(i) && (includeVisible || i.visible),
              ).length > 0,
          )
          .map((step, index) => (
            <div key={index} className="flex flex-col gap-y-10">
              <SegmentHeader
                className="max-w-lg"
                title={step.title}
                description={step.description}
              />

              <div className={twMerge(gridStyles({ size }), "grid-cols-1")}>
                {getVisibleFields(step.inputs).map((input) => (
                  <DynamicField
                    key={input.id}
                    field={input}
                    name={`fields.${input.id}`}
                    required={isFieldRequired(input)}
                  />
                ))}
              </div>
            </div>
          ))}

        {typeof children === "function"
          ? children({
              control: control,
              formData: watchedFormData,
            })
          : children}

        {showSkipButton || canReset || showSubmitButton ? (
          <div className="flex w-full items-center justify-center gap-x-6">
            {showSkipButton && (
              <Button intent="secondary" onPress={() => onSkip?.()}>
                Spring over
              </Button>
            )}

            {canReset && (
              <Button
                intent="secondary"
                onPress={() => {
                  onReset?.();
                }}
              >
                {resetLabel}
              </Button>
            )}

            {showSubmitButton && (
              <Button
                isDisabled={!methods.formState.isValid || disableSubmit}
                onPress={() => methods.trigger()}
                intent="primary"
                type="submit"
              >
                {submitLabel}
              </Button>
            )}
          </div>
        ) : null}

        {/* Debug info - removed in production */}
        {import.meta.env.DEV && (
          <details className="w-full">
            <summary className="text-theme-on-background-variant cursor-pointer text-sm">
              Debug Info
            </summary>

            <pre className="bg-theme-surface mt-2 overflow-auto rounded p-4 text-xs">
              <code>{debugInfo}</code>
            </pre>
          </details>
        )}
      </form>
    </FormProvider>
  );
}
